import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';

/**
 * The read and write hooks for docs/04-API-SPEC.md §6's resources.
 *
 * §6 gives every content type the same seven endpoints, and `ListQuery` on the
 * server gives every index the same query string — so the client half is
 * written once too. A module supplies its resource path and its row type; it
 * does not restate what "page 2 of a filtered list" means.
 *
 * These are deliberately not generated from the OpenAPI document like the
 * public site's hooks. Scramble publishes each admin resource's translatable
 * fields as `{[key: string]: unknown}` — it cannot see through the per-locale
 * map `AdminResource::translations()` builds — so a generated hook would type
 * `title` as unknown and every screen would narrow it anyway. The row type
 * comes from the module instead, checked against the resource it mirrors.
 */

/** The `meta`/`links` envelope from §1, identical on every index. */
export interface ListMeta {
  current_page: number;
  from: number | null;
  last_page: number;
  per_page: number;
  to: number | null;
  total: number;
}

export interface ListResponse<TRow> {
  data: TRow[];
  meta: ListMeta;
  links: { first: string | null; last: string | null; prev: string | null; next: string | null };
}

/** Everything `ListQuery` on the server understands, plus per-resource filters. */
export interface ListParams {
  search?: string;
  sort?: string;
  page?: number;
  per_page?: number;
  /** §4.6's "show deleted" toggle. */
  trashed?: '1' | 'with';
  [filter: string]: string | number | undefined;
}

/**
 * One key factory for the whole admin surface.
 *
 * Every mutation invalidates `['admin', resource]`, which covers every list
 * page and filter combination of that resource at once. Invalidating a single
 * page instead leaves the others stale, and the editor sees their new record
 * appear on page 1 and *also* still see the old page 3.
 */
export const adminKeys = {
  resource: (resource: string) => ['admin', resource] as const,
  list: (resource: string, params: ListParams) => ['admin', resource, 'list', params] as const,
  record: (resource: string, id: number | string) => ['admin', resource, 'record', id] as const,
};

export function useResourceList<TRow>(
  resource: string,
  params: ListParams = {},
): UseQueryResult<ListResponse<TRow>, Error> {
  return useQuery({
    queryKey: adminKeys.list(resource, params),
    queryFn: () => apiFetch<ListResponse<TRow>>(`/admin/${resource}`, { query: params }),
    /*
     * The previous page stays on screen while the next one loads. Without it
     * every keystroke in the search box blanks the table to a skeleton, which
     * makes a fast list feel broken.
     */
    placeholderData: (previous) => previous,
  });
}

export function useResourceRecord<TRow>(
  resource: string,
  id: number | string | null,
): UseQueryResult<{ data: TRow }, Error> {
  return useQuery({
    queryKey: adminKeys.record(resource, id ?? 'new'),
    queryFn: () => apiFetch<{ data: TRow }>(`/admin/${resource}/${String(id)}`),
    // `null` is the "new record" case — there is nothing to fetch.
    enabled: id !== null,
  });
}

export interface SaveVariables<TInput> {
  /** Absent for a create. */
  id?: number | string;
  input: TInput;
}

/**
 * Create and update in one hook, because a form does not know which it is until
 * the record it is editing has an id — and a screen that has to choose between
 * two hooks ends up choosing wrong on the save immediately after a create.
 */
export function useSaveResource<TRow, TInput>(
  resource: string,
): UseMutationResult<{ data: TRow }, Error, SaveVariables<TInput>> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: SaveVariables<TInput>) =>
      apiFetch<{ data: TRow }>(
        id === undefined ? `/admin/${resource}` : `/admin/${resource}/${String(id)}`,
        { method: id === undefined ? 'POST' : 'PUT', body: input },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.resource(resource) });
    },
  });
}

export type ResourceAction = 'delete' | 'restore' | 'force';

/**
 * §6's three lifecycle operations.
 *
 * `force` is separate from `delete` in the API because it is the one thing with
 * no undo, and it stays separate here so a screen cannot reach it by passing a
 * flag it forgot to guard.
 */
export function useResourceAction<TRow>(
  resource: string,
): UseMutationResult<
  { data: TRow } | void,
  Error,
  { id: number | string; action: ResourceAction }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, action }) => {
      const base = `/admin/${resource}/${String(id)}`;

      switch (action) {
        case 'restore':
          // The only one that answers with a body — §6 returns the restored
          // record so the list can show it without a second round trip.
          return await apiFetch<{ data: TRow }>(`${base}/restore`, { method: 'POST' });
        case 'force':
          await apiFetch<void>(`${base}/force`, { method: 'DELETE' });

          return undefined;
        default:
          await apiFetch<void>(base, { method: 'DELETE' });

          return undefined;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.resource(resource) });
    },
  });
}
